import React from 'react';
import { motion } from 'framer-motion';
import { LeafIcon, SunIcon, SnowflakeIcon, ArrowRightIcon } from 'lucide-react';
const SEASONS = [{
  id: 'spring',
  name: 'SPRING',
  title: 'Spring Cleaning',
  desc: 'Clear winter debris and prepare your gutters for heavy spring rains before damage occurs.',
  color: '#7AC943',
  gradient: 'linear-gradient(135deg, rgba(122,201,67,0.9) 0%, rgba(90,160,40,0.95) 100%)',
  icon: LeafIcon,
  animation: {
    y: [0, -8, 0],
    rotate: [0, 8, -4, 0]
  },
  duration: 4
}, {
  id: 'summer',
  name: 'SUMMER',
  title: 'Summer Maintenance',
  desc: 'Keep gutters clear and flowing during intense summer thunderstorms and heat.',
  color: '#FFD400',
  gradient: 'linear-gradient(135deg, rgba(255,212,0,0.95) 0%, rgba(230,180,0,0.95) 100%)',
  icon: SunIcon,
  animation: {
    rotate: 360
  },
  duration: 10
}, {
  id: 'fall',
  name: 'FALL',
  title: 'Fall Leaf Removal',
  desc: 'Remove fallen leaves before they cause massive clogs and water damage this season.',
  color: '#F97316',
  gradient: 'linear-gradient(135deg, rgba(249,115,22,0.9) 0%, rgba(220,80,10,0.95) 100%)',
  icon: LeafIcon,
  animation: {
    y: [-8, 10],
    x: [-4, 6, -4],
    rotate: [0, 30, 60, 30, 0]
  },
  duration: 3.5
}, {
  id: 'winter',
  name: 'WINTER',
  title: 'Winter Protection',
  desc: 'Prevent ice dams, icicles, and costly winter water damage before the freeze hits.',
  color: '#7DD3FC',
  gradient: 'linear-gradient(135deg, rgba(125,211,252,0.9) 0%, rgba(56,189,248,0.95) 100%)',
  icon: SnowflakeIcon,
  animation: {
    rotate: -360,
    scale: [1, 1.1, 1]
  },
  duration: 8
}];
export function SeasonsSection() {
  return <section className="relative py-24 bg-white overflow-hidden" data-id="element-286">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 100%, rgba(11,122,196,0.04) 0%, transparent 60%)'
    }} data-id="element-287" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-288">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-289">
          <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-290">
            Our Brand Promise
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4" data-id="element-291">
            Serving You Through Every Season
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] mx-auto rounded-full mb-6" data-id="element-292" />
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light" data-id="element-293">
            New Jersey weather is unpredictable. We keep your home protected 365
            days a year.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-294">
          {SEASONS.map((season, index) => <motion.div key={season.id} initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.12,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} whileHover={{
          y: -6,
          scale: 1.02
        }} className="relative rounded-3xl p-8 overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300" style={{
          background: season.gradient
        }} data-id="element-295">
              {/* Decorative circles */}
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 pointer-events-none" data-id="element-296" />
              <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-black/10 pointer-events-none" data-id="element-297" />

              <div className="relative z-10" data-id="element-298">
                {/* Animated icon */}
                <motion.div animate={season.animation} transition={{
              duration: season.duration,
              repeat: Infinity,
              ease: season.id === 'summer' || season.id === 'winter' ? 'linear' : 'easeInOut'
            }} className="mb-6 inline-block" data-id="element-299">
                  <season.icon className="w-12 h-12 text-white drop-shadow-lg" data-id="element-300" />
                </motion.div>

                <div className="text-white/80 font-black tracking-[0.2em] text-xs mb-2" data-id="element-301">
                  {season.name}
                </div>
                <h3 className="text-2xl font-black text-white mb-3 leading-tight" style={{
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }} data-id="element-302">
                  {season.title}
                </h3>
                <p className="text-white/90 text-sm leading-relaxed font-medium" data-id="element-303">
                  {season.desc}
                </p>

                {/* CTA link */}
                <a href="#contact" className="mt-6 inline-flex items-center gap-1.5 text-white font-bold text-sm hover:gap-3 transition-all duration-200" data-id="element-304">
                  Schedule Now <ArrowRightIcon className="w-4 h-4" data-id="element-305" />
                </a>
              </div>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="mt-16 text-center" data-id="element-306">
          <a href="#contact" className="btn-primary inline-flex items-center gap-2 text-lg" data-id="element-307">
            Schedule Service for Any Season
            <ArrowRightIcon className="w-5 h-5" data-id="element-308" />
          </a>
        </motion.div>
      </div>
    </section>;
}